import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';
import {ConnectionLineComponentProps, getSmoothStepPath} from 'reactflow';
import _ from 'underscore';

import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, RED} from 'ui-library/Colors';

import {getHandleTypeDetails} from './handleIdentifierUtils';
import {ProcedureEditorContext} from './ProcedureEditorContext';

const CORRECT_DIRECTION_BUFFER = 20;

export interface ProcedureConnectionLineRendererProps extends ConnectionLineComponentProps {
  isActivelyConnecting: boolean;
}

export default function ProcedureConnectionLineRenderer({
  fromX,
  fromY,
  fromPosition,
  fromHandle,
  toX,
  toY,
  toPosition,
  connectionStatus,
  isActivelyConnecting,
}: ProcedureConnectionLineRendererProps): JSX.Element {
  const {invalidMaterialHandles, setInvalidMaterialHandles} = ProcedureEditorContext.useContext();

  let isMovingInCorrectDirection = false;
  if (fromHandle?.id != null) {
    const isSourceHandle = getHandleTypeDetails(fromHandle.id).handleType === 'source';
    // Source handles must have connection moving downstream
    // Target handles must have connection moving upstream
    isMovingInCorrectDirection = isSourceHandle
      ? fromX <= toX + CORRECT_DIRECTION_BUFFER
      : fromX >= toX - CORRECT_DIRECTION_BUFFER;
  }

  const isValidConnectionLine = isMovingInCorrectDirection && connectionStatus !== 'invalid';

  useEffect(() => {
    if (fromHandle?.id != null) {
      if (!isActivelyConnecting || isValidConnectionLine) {
        setInvalidMaterialHandles(invalidMaterialHandles.filter((handle) => handle !== fromHandle.id));
      } else {
        setInvalidMaterialHandles(_.unique(invalidMaterialHandles.concat(fromHandle.id)));
      }
    }
  }, [
    fromHandle,
    invalidMaterialHandles,
    isActivelyConnecting,
    isValidConnectionLine,
    setInvalidMaterialHandles,
  ]);

  const [connectionLinePath] = getSmoothStepPath({
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: fromPosition,
    targetX: toX,
    targetY: toY,
    targetPosition: toPosition,
  });

  return (
    <g>
      <path
        fill="none"
        strokeWidth={1.5}
        className={css(isValidConnectionLine ? styles.valid : styles.invalid)}
        d={connectionLinePath}
        {...getTestProps({
          component: 'ProcedureMaterialEdgeConnectionLine',
          element: 'path',
        })}
      />
    </g>
  );
}
const styles = StyleSheet.create({
  invalid: {
    stroke: RED,
  },
  valid: {
    stroke: GRAY,
  },
});
